import React, { useEffect, useRef, useState, useCallback } from "react";

// Loads the Google Maps JS API exactly once (singleton promise)
let mapsPromise = null;
export const loadGoogleMaps = () => {
  if (mapsPromise) return mapsPromise;
  const key = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (!key) {
    mapsPromise = Promise.reject(new Error("missing REACT_APP_GOOGLE_MAPS_KEY"));
    return mapsPromise;
  }
  mapsPromise = new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google);
      return;
    }
    const cb = `__gmaps_cb_${Date.now()}`;
    window[cb] = () => { resolve(window.google); delete window[cb]; };
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=weekly&libraries=places&callback=${cb}&loading=async`;
    s.async = true; s.defer = true;
    s.onerror = () => reject(new Error("Failed to load Google Maps JS"));
    document.head.appendChild(s);
  });
  return mapsPromise;
};

export default function PlacesInput({
  value,
  onChange,
  placeholder,
  testId,
  className = "field-input",
  country = "us",
  autoFocus = false,
}) {
  const inputRef = useRef(null);
  // Focus on mount (after the page has scrolled to the top) — used by the booking form
  useEffect(() => {
    if (!autoFocus || !inputRef.current) return;
    const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 150);
    return () => clearTimeout(t);
  }, [autoFocus]);
  const wrapperRef = useRef(null);
  const sessionRef = useRef(null);
  const placesLibRef = useRef(null);
  const debounceRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(async (g) => {
        if (cancelled) return;
        // Load the modern places library
        const places = await g.maps.importLibrary("places");
        if (cancelled) return;
        placesLibRef.current = places;
        sessionRef.current = new places.AutocompleteSessionToken();
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("PlacesInput load fallback:", e.message);
      });
    return () => { cancelled = true; };
  }, []);

  // Click-away to close
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchPredictions = useCallback(async (input) => {
    const places = placesLibRef.current;
    if (!places || !input || input.length < 2) {
      setSuggestions([]); setOpen(false); return;
    }
    try {
      const { suggestions: results } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionRef.current,
        includedRegionCodes: [country],
      });
      const formatted = (results || []).map((s) => {
        const p = s.placePrediction;
        if (!p) return null;
        return {
          id: p.placeId,
          main: p.mainText?.text || p.text?.text || "",
          secondary: p.secondaryText?.text || "",
          full: p.text?.text || `${p.mainText?.text || ""} ${p.secondaryText?.text || ""}`.trim(),
        };
      }).filter(Boolean).slice(0, 6);
      setSuggestions(formatted);
      setOpen(formatted.length > 0);
      setActiveIdx(-1);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("AutocompleteSuggestion error:", e.message);
      setSuggestions([]); setOpen(false);
    }
  }, [country]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(v), 180);
  };

  const pick = (s) => {
    onChange(s.full);
    setSuggestions([]);
    setOpen(false);
    // Refresh session token after a successful selection (billing best practice)
    if (placesLibRef.current) {
      sessionRef.current = new placesLibRef.current.AutocompleteSessionToken();
    }
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative places-input-wrap">
      <input
        ref={inputRef}
        data-testid={testId}
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul
          data-testid={testId ? `${testId}-suggestions` : "places-suggestions"}
          className="places-dropdown absolute left-0 right-0 mt-1 z-[10000] rounded-md shadow-2xl max-h-72 overflow-auto"
        >
          {suggestions.map((s, idx) => (
            <li
              key={s.id}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`places-dropdown-item px-3 py-2 cursor-pointer text-sm ${idx === activeIdx ? "is-active" : ""}`}
            >
              <div className="places-dropdown-main">{s.main}</div>
              {s.secondary && (
                <div className="places-dropdown-sub text-xs mt-0.5">{s.secondary}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
